import { OpenSearchQueryBuilder, classifySearchToken } from 'tio-common/utils/open-search-builder';
import { csvToKeys } from 'tio-common/utils/people-facet-labels';
import {
  EMPLOYEES_PATH,
  EMPLOYEE_STATUS_BUCKETS,
  ENROLLMENT_BUCKETS,
  PARTICIPATION_BUCKETS,
  PARTNER_VISIBLE_ROLE_KEYS,
  bucketSelectionClause,
  companyEmployeeClause,
} from 'tio-employee/utils/people-facets';
import type { FilterClause } from 'tio-common/utils/open-search-builder';

/** Facet selections as they arrive from the URL — comma-separated bucket keys. */
export interface PeopleSearchParams {
  search?: string;
  status?: string;
  enrollment?: string;
  participationStatus?: string;
  jurisdictions?: string;
  page?: number | string;
  limit?: number | string;
}

/** Response key for the matched employments. Chosen to survive the API's camelization unchanged. */
export const EMPLOYEE_MATCHES = 'employments';

/** Aggregation holding the whole-result-set count of exact name matches. */
export const EXACT_NAME_AGG = 'exactNameMatches';

/**
 * Root-relative paths: inner_hits resolves `_source` includes from the document
 * root, so dropping the `employees_nested.` prefix returns an empty object.
 */
const EMPLOYEE_MATCH_FIELDS = [
  'employees_nested.employee_id_keyword',
  'employees_nested.first_name_text',
  'employees_nested.last_name_text',
  'employees_nested.payroll_id_keyword',
  'employees_nested.email_text',
  'employees_nested.participation_status_keyword',
  'employees_nested.created_at_date',
  'employees_nested.jurisdictions_keyword',
  'employees_nested.employee_is_active_boolean',
  'employees_nested.employee_is_enrolled_boolean',
  'employees_nested.employee_is_registered_boolean',
];

/**
 * Person-grain fields on the parent document. Deliberately minimal: everything
 * the cards render comes from `inner_hits`, which carries only this company's
 * employment.
 */
const PERSON_SOURCE_FIELDS = ['person_id_keyword'];

/** The single matched employment returned per person, projected into a card. */
function innerHits() {
  return { name: EMPLOYEE_MATCHES, size: 1, _source: EMPLOYEE_MATCH_FIELDS };
}

/** Name fields inside the nested employment — never the person-grain `all_employee_names`. */
const NAME_FIELDS = ['employees_nested.first_name_text', 'employees_nested.last_name_text'];

/** Largest page OpenSearch will serve here; `limit` is user-controlled via the URL. */
const MAX_LIMIT = 100;
const DEFAULT_LIMIT = 25;

/** The four facet groups, in the order the rail renders them. */
type FacetGroup = 'status' | 'enrollment' | 'participation' | 'roles';

/** True when the search box holds at least one name token (as opposed to only ids/emails). */
export function hasNameTokens(search?: string): boolean {
  return tokenize(search).nameTokens.length > 0;
}

/**
 * Builds company-scoped people search, clamping malformed page/limit values without throwing.
 * Identity predicates share employees_nested with the company term; person rollups span employers.
 * Exclude the parent employment array and project matching inner_hits, since source filtering cannot hide array elements.
 * Facets share that company boundary and role keys use an allowlist to prevent internal-role disclosure.
 */
export function buildPeopleSearchBody(
  companyId: string,
  params: PeopleSearchParams
): Record<string, unknown> {
  const { search, status, enrollment, participationStatus, jurisdictions, page, limit } = params;

  const safePage = clampPositiveInt(page, 1, Number.MAX_SAFE_INTEGER, 1);
  const safeLimit = clampPositiveInt(limit, 1, MAX_LIMIT, DEFAULT_LIMIT);

  const builder = new OpenSearchQueryBuilder().setPagination(safePage, safeLimit);
  const companyScope = companyEmployeeClause(companyId);

  // The top-level term satisfies SearchPolicy, which requires a company filter.
  builder.mustTerm('company_ids', companyId);

  const identityClauses = identitySearchClauses(search);
  const searching = identityClauses.length > 0;

  // inner_hits rides on whichever nested clause decides the matched employment.
  // OpenSearch reports `matched_queries` on an inner hit only for named clauses
  // evaluated inside THAT inner_hits query, so during a search it has to sit on
  // the scoring clause — hanging it off the plain company filter would return
  // the right row with no record of what matched, and the match chips and
  // exact-match border would silently never render.
  builder.addFilter({
    nested: {
      path: EMPLOYEES_PATH,
      ...(searching ? {} : { inner_hits: innerHits() }),
      query: { bool: { filter: [companyScope] } },
    },
  } as FilterClause);
  builder.selectSourceFields(PERSON_SOURCE_FIELDS, []);

  if (searching) {
    // Scoring context (not `filter`): filtered clauses score 0, which ties every
    // match and makes relevance ordering — and so the exact-match ranking the
    // cards rely on — arbitrary.
    builder.addSearchShould({
      nested: {
        path: EMPLOYEES_PATH,
        // max, not the avg default: rank the person by their best-matching employment.
        score_mode: 'max',
        inner_hits: innerHits(),
        query: {
          bool: {
            filter: [companyScope],
            should: identityClauses,
            minimum_should_match: 1,
          },
        },
      },
    });
  }

  const roleKeys = csvToKeys(jurisdictions).filter((key) =>
    PARTNER_VISIBLE_ROLE_KEYS.includes(key)
  );

  // Every group goes into post_filter so each group's aggregation can keep its
  // sibling counts; each group's aggregation is filtered by the OTHER groups
  // (full cross-facet counts, Amazon-style). These are the INNER clauses —
  // dotted `employees_nested.*` paths valid inside the nested scope.
  const groupClauses: Record<FacetGroup, FilterClause | undefined> = {
    status: bucketSelectionClause(EMPLOYEE_STATUS_BUCKETS, status ?? ''),
    enrollment: bucketSelectionClause(ENROLLMENT_BUCKETS, enrollment ?? ''),
    participation: bucketSelectionClause(PARTICIPATION_BUCKETS, participationStatus ?? ''),
    roles: roleKeys.length
      ? { terms: { 'employees_nested.jurisdictions_keyword': roleKeys } }
      : undefined,
  };

  for (const clause of Object.values(groupClauses)) {
    // The company clause rides along inside every wrapper: a bare nested
    // selection would otherwise match a person via ANOTHER company's entry.
    if (clause) {
      builder.addPostFilter({
        nested: {
          path: EMPLOYEES_PATH,
          query: { bool: { filter: [companyScope, clause] } },
        },
      } as FilterClause);
    }
  }

  // Company scope first, then every group except the one being counted.
  const othersOf = (group: FacetGroup): FilterClause[] => [
    companyScope,
    ...Object.entries(groupClauses)
      .filter(([name, clause]) => name !== group && clause)
      .map(([, clause]) => clause as FilterClause),
  ];

  builder.addNestedFilteredNamedFiltersAggregation(
    'facetStatus',
    EMPLOYEES_PATH,
    othersOf('status'),
    EMPLOYEE_STATUS_BUCKETS
  );
  builder.addNestedFilteredNamedFiltersAggregation(
    'facetEnrollment',
    EMPLOYEES_PATH,
    othersOf('enrollment'),
    ENROLLMENT_BUCKETS
  );
  builder.addNestedFilteredNamedFiltersAggregation(
    'facetParticipation',
    EMPLOYEES_PATH,
    othersOf('participation'),
    PARTICIPATION_BUCKETS
  );
  builder.addNestedFilteredTermsAggregation(
    'facetRoles',
    EMPLOYEES_PATH,
    'employees_nested.jurisdictions_keyword',
    othersOf('roles'),
    50,
    // Bucket the allowlist in the query. Filtering client-side would still have
    // disclosed TIO-internal role keys and their per-company counts on the wire.
    { include: PARTNER_VISIBLE_ROLE_KEYS }
  );

  // Whole-result-set exact-name count. The card list can't infer this from page
  // one: an exact `should` clause raises a hit's score but does not guarantee it
  // outranks every fuzzy match, so an exact hit can sit on a later page.
  const exactClause = exactNameClause(search);
  if (exactClause) {
    builder.addNestedFilteredNamedFiltersAggregation(
      EXACT_NAME_AGG,
      EMPLOYEES_PATH,
      [
        companyScope,
        ...Object.values(groupClauses)
          .filter(Boolean)
          .map((c) => c as FilterClause),
      ],
      { exact: exactClause }
    );
  }

  const body = builder.build() as Record<string, unknown>;

  // With a search, relevance ordering is what surfaces the best matches first.
  // Without one every hit scores the same, so fall back to a company-scoped
  // nested date sort to keep pagination stable.
  if (!searching) {
    body.sort = nestedSort(companyId);
  }

  return body;
}

/**
 * Coerce a URL-supplied number to an integer within [min, max].
 *
 * Rejects non-finite values and truncates decimals: `setPagination` turns these
 * straight into `from`/`size`, where a fractional or `Infinity` offset is an
 * OpenSearch error and an oversized `size` is a cheap way to make the cluster
 * do a lot of work from a query string.
 */
function clampPositiveInt(
  value: number | string | undefined,
  min: number,
  max: number,
  fallback: number
): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(parsed) || fallback));
}

/** Split the search box into identifier-ish tokens and name tokens. */
function tokenize(search?: string): { tokens: string[]; nameTokens: string[] } {
  const trimmed = search?.trim();
  if (!trimmed) return { tokens: [], nameTokens: [] };

  const tokens = trimmed.split(/\s+/).filter(Boolean);
  const nameTokens = tokens.filter((token) => classifySearchToken(token) === 'name');
  return { tokens, nameTokens };
}

/**
 * Identity clauses for the search box, every one addressing a field INSIDE the
 * nested employment. Tokens are classified (email / identifier / name) and
 * routed to the matching field; clauses are OR-combined by the caller and
 * tagged with `_name` so each inner hit reports what it matched on.
 */
function identitySearchClauses(search?: string): FilterClause[] {
  const { tokens, nameTokens } = tokenize(search);
  if (!tokens.length) return [];

  const clauses: FilterClause[] = [];

  for (const token of tokens) {
    const kind = classifySearchToken(token);
    if (kind === 'email') {
      clauses.push({
        match: {
          'employees_nested.email_text': { query: token, operator: 'and', _name: 'email' },
        },
      });
    } else if (kind === 'identifier') {
      clauses.push({
        term: { 'employees_nested.payroll_id_keyword': { value: token, _name: 'payroll' } },
      });
      clauses.push({
        term: { 'employees_nested.employee_id_keyword': { value: token, _name: 'employee_id' } },
      });
    }
  }

  const exact = exactNameClause(search);
  if (exact) {
    // Partial: any token may match (typo-tolerant) — this is what broadens the set.
    clauses.push({
      multi_match: {
        query: nameTokens.join(' '),
        fields: NAME_FIELDS,
        type: 'best_fields',
        operator: 'or',
        fuzziness: 'AUTO',
        lenient: true,
        _name: 'name',
      },
    });
    clauses.push(exact);
  }

  return clauses;
}

/**
 * Every name token present with NO fuzziness. Tagged `name_exact` so an inner
 * hit can be flagged as an exact match, and reused as the exact-count
 * aggregation bucket. Undefined when the search holds no name tokens.
 */
function exactNameClause(search?: string): FilterClause | undefined {
  const { nameTokens } = tokenize(search);
  if (!nameTokens.length) return undefined;

  return {
    multi_match: {
      query: nameTokens.join(' '),
      fields: NAME_FIELDS,
      type: 'cross_fields',
      operator: 'and',
      lenient: true,
      _name: 'name_exact',
    },
  };
}

/**
 * Newest employee first, read from THIS company's nested entry, with a stable
 * `employee_id_keyword` tiebreaker so `from` pagination can't duplicate or skip
 * rows when the dates tie.
 */
function nestedSort(companyId: string) {
  const companyNested = {
    path: EMPLOYEES_PATH,
    filter: { term: { 'employees_nested.company_id_keyword': companyId } },
  };

  return [
    { 'employees_nested.created_at_date': { order: 'desc', nested: companyNested } },
    { 'employees_nested.employee_id_keyword': { order: 'asc', nested: companyNested } },
  ];
}
